import { cn } from "@/lib/utils";

/**
 * Brand loading indicator (the Tippo hippo).
 *
 * Renders the animated hippo GIF with a pulsing three-dot row beneath it. The
 * dots animate via CSS so progress is still communicated if the GIF is missing
 * or slow to load. Server-renderable so it can be used directly as a Suspense
 * fallback and streamed before any data resolves.
 *
 * Brand asset lives at `apps/web/public/hippo-loading.gif`.
 */

interface ITippoLoadingIndicatorProps {
  /** Pixel size of the hippo mark. Defaults to 96. */
  size?: number;
  /** Optional label announced to assistive tech. Defaults to "Loading". */
  label?: string;
  className?: string;
}

export function TippoLoadingIndicator({
  size = 96,
  label = "Loading",
  className,
}: Readonly<ITippoLoadingIndicatorProps>): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cn("flex flex-col items-center justify-center gap-4", className)}
    >
      {/* Animated GIF: next/image would add overhead on this critical path. */}
      <img
        src="/hippo-loading.gif"
        alt=""
        width={size}
        height={size}
        className="select-none"
        style={{ width: size, height: size }}
        draggable={false}
      />
      <div aria-hidden="true" className="flex items-center gap-1.5">
        <span className="h-2 w-2 animate-bounce rounded-full bg-primary/70 [animation-delay:-0.3s] motion-reduce:animate-none" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-primary/70 [animation-delay:-0.15s] motion-reduce:animate-none" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-primary/70 motion-reduce:animate-none" />
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * Full-screen centered loading screen for route/layout-level Suspense fallbacks.
 * Fills the dashboard viewport so users see the hippo immediately instead of a
 * blank or half-rendered shell while server data resolves.
 */
export function DashboardLoadingScreen({
  label = "Loading your dashboard",
}: Readonly<{ label?: string }>): React.ReactElement {
  return (
    <div className="flex min-h-svh w-full items-center justify-center bg-background p-6">
      <TippoLoadingIndicator label={label} />
    </div>
  );
}
